import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// ─────────────────────────────────────────────────────────────
// BFF session model
// Authentication is via an HttpOnly session cookie set by the
// backend after the ctOneAuth OIDC callback. The browser sends
// it automatically (same-origin in dev via Vite proxy and prod
// behind pfSense). We only manage the active company id here.
// ─────────────────────────────────────────────────────────────

class CompanyManager {
  private companyId: string | null = null;

  setCompanyId(id: string | null) {
    this.companyId = id;
    if (id) localStorage.setItem('ctticket-company', id);
    else localStorage.removeItem('ctticket-company');
  }

  getCompanyId(): string | null {
    return this.companyId || localStorage.getItem('ctticket-company');
  }

  // Legacy token shims — retained so older callers don't break while
  // they migrate off. The BFF cookie carries auth, so these are no-ops.
  getToken(): string | null { return null; }
  setToken(_: string | null) { /* no-op under BFF */ }
  async refresh(): Promise<string | null> { return null; }
}

export const tokenManager = new CompanyManager();

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30_000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ─────────────────────────────────────────────────────────────
// CSRF — the backend issues a session-bound token at GET /api/auth/csrf.
// We fetch it lazily, cache it, and attach it to every mutating request.
// ─────────────────────────────────────────────────────────────
const SAFE_METHODS = new Set(['get', 'head', 'options']);
let csrfToken: string | null = null;
let csrfInFlight: Promise<string | null> | null = null;

async function fetchCsrfToken(): Promise<string | null> {
  if (csrfInFlight) return csrfInFlight;
  csrfInFlight = axios
    .get('/api/auth/csrf', { withCredentials: true })
    .then(r => {
      csrfToken = r.data?.data?.csrfToken ?? null;
      return csrfToken;
    })
    .catch(() => null)
    .finally(() => {
      csrfInFlight = null;
    });
  return csrfInFlight;
}

export function clearCsrfToken() {
  csrfToken = null;
}

/** The cached token, for callers that must build a request by hand (a
 *  keepalive fetch on page hide, where axios can't be used). Null until the
 *  first mutating request has fetched one. */
export function getCachedCsrfToken(): string | null {
  return csrfToken;
}

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const companyId = tokenManager.getCompanyId();
  if (companyId) config.headers['X-Company-Id'] = companyId;

  const method = (config.method || 'get').toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const token = csrfToken || (await fetchCsrfToken());
    if (token) config.headers['X-CSRF-Token'] = token;
  }
  return config;
});

apiClient.interceptors.response.use(
  r => r,
  async error => {
    // Stale/missing CSRF token — refetch once and replay the original request.
    const status = error.response?.status;
    const csrfFailed =
      status === 403 && /csrf/i.test(error.response?.data?.message || '');
    const original = error.config as (InternalAxiosRequestConfig & { _csrfRetried?: boolean }) | undefined;
    if (csrfFailed && original && !original._csrfRetried) {
      original._csrfRetried = true;
      clearCsrfToken();
      const token = await fetchCsrfToken();
      if (token) {
        original.headers = original.headers || {};
        (original.headers as Record<string, string>)['X-CSRF-Token'] = token;
        return apiClient(original);
      }
    }

    // Cookie-based session expired — bounce the user to the login page.
    if (status === 401) {
      const onAuthPage =
        window.location.pathname.startsWith('/login') ||
        window.location.pathname.startsWith('/register') ||
        window.location.pathname.startsWith('/portal');
      if (!onAuthPage) {
        const returnTo = window.location.pathname + window.location.search;
        window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
      }
    }
    const apiMsg = error.response?.data?.message;
    return Promise.reject(apiMsg ? new Error(apiMsg) : error);
  }
);
